import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress, MenuItem, FormControlLabel, Switch,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

const useFetchFeBaseList = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-base/`)
      .then(res => setList(res.data))
      .catch(() => setList([]));
  }, []);
  return list;
};

export const AdminFeValidityMasterComponent = () => {
  const feBaseList = useFetchFeBaseList();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formFields, setFormFields] = useState({
    financial_establishment: { value: '', error: false, helperText: '' },
    normal_start_date: { value: '', error: false, helperText: '' },
    normal_end_date: { value: '', error: false, helperText: '' },
    delay_start_date: { value: '', error: false, helperText: '' },
    delay_end_date: { value: '', error: false, helperText: '' },
    is_active: { value: false },
  });
  const [uploading, setUploading] = useState(false);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'financial_establishment', field: 'financial_establishment', headerName: 'FE Base', width: 200 },
    { id: 'normal_start_date', field: 'normal_start_date', headerName: 'Normal Start Date', width: 150 },
    { id: 'normal_end_date', field: 'normal_end_date', headerName: 'Normal End Date', width: 150 },
    { id: 'delay_start_date', field: 'delay_start_date', headerName: 'Delay Start Date', width: 150 },
    { id: 'delay_end_date', field: 'delay_end_date', headerName: 'Delay End Date', width: 150 },
    {
      id: 'is_active',
      field: 'is_active',
      headerName: 'Is Active',
      width: 100,
      renderCell: (params) => (params.value ? 'Yes' : 'No'),
    },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}><Edit /></IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}><Delete /></IconButton>
        </>
      ),
    },
  ];

  useEffect(() => {
    if (feBaseList.length > 0) fetchValidityRecords();
  }, [feBaseList]);

  const fetchValidityRecords = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-validity/`)
      .then(response => {
        const apiData = response.data.map((item, index) => {
          const feBaseObj = feBaseList.find(fe => fe.id === (item.financial_establishment?.id || item.financial_establishment));
          return {
            id: item.id,
            slNo: index + 1,
            financial_establishment: feBaseObj ? feBaseObj.name_of_fe : (item.financial_establishment?.name_of_fe || item.financial_establishment),
            normal_start_date: item.normal_start_date,
            normal_end_date: item.normal_end_date,
            delay_start_date: item.delay_start_date,
            delay_end_date: item.delay_end_date,
            is_active: item.is_active,
          };
        });
        setRows(apiData);
        setLoading(false);
      })
      .catch(() => {
        setSnackbarMessage('Failed to load validity records.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const resetFormFields = () => {
    setFormFields({
      financial_establishment: { value: '', error: false, helperText: '' },
      normal_start_date: { value: '', error: false, helperText: '' },
      normal_end_date: { value: '', error: false, helperText: '' },
      delay_start_date: { value: '', error: false, helperText: '' },
      delay_end_date: { value: '', error: false, helperText: '' },
      is_active: { value: false },
    });
  };

  const handleAddItem = () => {
    setIsEditMode(false);
    resetFormFields();
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    const feBaseObj = feBaseList.find(fe => fe.name_of_fe === item.financial_establishment || fe.id === item.financial_establishment);
    setFormFields({
      financial_establishment: { value: feBaseObj ? feBaseObj.id : '', error: false, helperText: '' },
      normal_start_date: { value: item.normal_start_date, error: false, helperText: '' },
      normal_end_date: { value: item.normal_end_date, error: false, helperText: '' },
      delay_start_date: { value: item.delay_start_date, error: false, helperText: '' },
      delay_end_date: { value: item.delay_end_date, error: false, helperText: '' },
      is_active: { value: item.is_active || false },
    });
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/fe-mgmt/fe-validity/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('Validity record deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchValidityRecords();
      })
      .catch(() => {
        setSnackbarMessage('Failed to delete validity record.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  const validateField = (name, value) => {
    if (!value) {
      switch (name) {
        case 'financial_establishment': return 'FE Base is required';
        case 'normal_start_date': return 'Normal start date is required';
        case 'normal_end_date': return 'Normal end date is required';
        // case 'delay_start_date': return 'Delay start date is required';
        // case 'delay_end_date': return 'Delay end date is required';
        default: return '';
      }
    }
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const errMsg = validateField(name, value);
    setFormFields(prev => ({
      ...prev,
      [name]: { value, error: !!errMsg, helperText: errMsg },
    }));
  };

  const handleSwitchChange = (e) => {
    setFormFields(prev => ({
      ...prev,
      is_active: { value: e.target.checked }
    }));
  }

  const validateForm = () => {
    let isValid = true;
    const newFields = { ...formFields };
    Object.keys(newFields).forEach(key => {
      if (key !== 'is_active') {
        const errMsg = validateField(key, newFields[key].value);
        if (errMsg) isValid = false;
        newFields[key] = { ...newFields[key], error: !!errMsg, helperText: errMsg };
      }
    });
    setFormFields(newFields);
    return isValid;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    // Prepare payload flat structure for API
    const payload = {
      financial_establishment: formFields.financial_establishment.value,
      normal_start_date: formFields.normal_start_date.value,
      normal_end_date: formFields.normal_end_date.value,
      delay_start_date: formFields.delay_start_date.value,
      delay_end_date: formFields.delay_end_date.value,
      is_active: formFields.is_active.value,
    };

    try {
      setUploading(true);
      if (isEditMode) {
        await api.patch(`/fe-mgmt/fe-validity/${selectedItem.id}/`, payload);
        setSnackbarMessage('Validity record updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-validity/`, payload);
        setSnackbarMessage('Validity record added successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchValidityRecords();
      setOpenFormDialog(false);
    } catch {
      setSnackbarMessage('Failed to submit validity record.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <LoadingSpinner size={30} />;

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', bgcolor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          FE Validity Records
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>
          Add
        </Button>
      </Box>
      <TableComponent
        columns={columns}
        rows={rows}
        manageColumnStatus
        enableSort
        sx={{ '& .MuiDataGrid-columnHeaders': { bgcolor: '#1976d2', color: '#fff', fontWeight: 'bold' } }}
      />
      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Validity Record' : 'Add Validity Record'}</DialogTitle>
        <DialogContent>
          <TextField select margin="dense" label="FE Base" fullWidth
            name="financial_establishment"
            value={formFields.financial_establishment.value}
            error={formFields.financial_establishment.error}
            helperText={formFields.financial_establishment.helperText}
            onChange={handleInputChange}
            disabled={uploading}>
            {feBaseList.map(fe => <MenuItem key={fe.id} value={fe.id}>{fe.name_of_fe || fe.id}</MenuItem>)}
          </TextField>
          {['normal_start_date', 'normal_end_date', 'delay_start_date', 'delay_end_date'].map(field => (
            <TextField
              key={field}
              margin="dense"
              label={field.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())}
              type="date"
              fullWidth
              name={field}
              value={formFields[field].value}
              error={formFields[field].error}
              helperText={formFields[field].helperText}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              disabled={uploading}
            />
          ))}
          <FormControlLabel
            control={
              <Switch
                checked={formFields.is_active.value}
                onChange={handleSwitchChange}
                disabled={uploading}
              />
            }
            label="Is Active"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>
            {isEditMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this validity record?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>Delete</Button>
        </DialogActions>
      </Dialog>
      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
